import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import FullscreenPanel from "@/components/FullscreenPanel";
import {
  LineChart, Line, Area, ComposedChart, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, ReferenceLine, ReferenceDot, Label as RLabel
} from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";
import { getCommodity } from "@/lib/commodities";

export default function GradeTonnagePanel({ projectId, refreshKey, commodity, ssotOre = null }) {
  const gradeUnit = getCommodity(commodity).grade_unit;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cutoff, setCutoff] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${projectId}/analytics/grade-tonnage`)
      .then((r) => {
        setData(r.data);
        setCutoff(r.data?.cutoff_grade ?? null);
      })
      .catch(() => setData({ points: [], max_grade: 0 }))
      .finally(() => setLoading(false));
  }, [projectId, refreshKey]);

  const rawPoints = data?.points || [];
  // Iter 70 · Feb 2026: platform-wide rebrand to Mt. Backend still emits
  // `tonnage_kt` / `waste_kt` (kilotonnes); we scale to Mt on the fly so
  // every chart axis reads `Mt` and legend labels never say `kt`.
  const points = useMemo(
    () => rawPoints.map((p) => ({
      ...p,
      tonnage_mt: (p.tonnage_kt || 0) / 1_000,
      waste_mt:   (p.waste_kt   || 0) / 1_000,
    })),
    [rawPoints],
  );
  // Snap cutoff to the nearest point so ReferenceDot lands on the curve
  const snapPoint = useMemo(() => {
    if (cutoff == null || !points.length) return null;
    let best = points[0]; let bd = Math.abs(points[0].cutoff - cutoff);
    for (const p of points) {
      const d = Math.abs(p.cutoff - cutoff);
      if (d < bd) { best = p; bd = d; }
    }
    return best;
  }, [cutoff, points]);

  if (loading) {
    return (
      <div className="border border-border bg-card p-4">
        <div className="smin-label mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" /> Grade-tonnage curve
        </div>
        <div className="h-40 grid place-items-center text-muted-foreground font-mono text-xs">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || points.length === 0) {
    return (
      <div className="border border-border bg-card p-4">
        <div className="smin-label mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" /> Grade-tonnage curve
        </div>
        <div className="text-sm text-muted-foreground">Create a block model to see the G-T curve.</div>
      </div>
    );
  }

  return (
    <FullscreenPanel title="Grade-tonnage curve" testid="grade-tonnage">
    <div className="border border-border bg-card p-4 relative pr-12" data-testid="grade-tonnage-panel">
      <div className="smin-label mb-2 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-primary" /> Grade-tonnage curve
        <span className="ml-auto text-muted-foreground normal-case">{data.block_model_name || ""}</span>
      </div>
      {snapPoint && (
        <div className="mb-2 flex items-center gap-3 font-mono text-[10px] text-muted-foreground border-l-2 border-primary pl-2"
          data-testid="gt-cutoff-readout">
          <span>Cutoff <span className="text-primary">{cutoff.toFixed(2)}</span></span>
          {/* Iter 70 · Feb 2026: kt labels removed platform-wide. Above-cutoff
              tonnage is shown in absolute Mt (2 decimals); the SSoT ore rollup
              from `class_summary.by_kind.ore.tonnage` is overlaid for cross-check. */}
          <span>Above cutoff <span className="text-foreground">{((snapPoint.tonnage_kt || 0) / 1_000).toFixed(2)} Mt</span></span>
          {ssotOre != null && (
            <span title="From material-class rollup (Single Source of Truth)">
              Ore (SSoT) <span className="text-foreground">{(ssotOre / 1_000_000).toFixed(2)} Mt</span>
            </span>
          )}
          <span>Avg g <span className="text-secondary">{snapPoint.avg_grade}</span></span>
          <span>SR <span className="text-foreground">{snapPoint.strip_ratio}</span></span>
        </div>
      )}
      <div style={{ height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={{ top: 10, right: 12, bottom: 4, left: -6 }}>
            <CartesianGrid stroke="#1F2430" />
            <XAxis dataKey="cutoff"
              tick={{ fontSize: 10, fill: "#94A3B8", fontFamily: "JetBrains Mono" }}
              label={{ value: `Cutoff (${gradeUnit})`, fill: "#94A3B8", fontSize: 10, dy: 10 }} />
            <YAxis yAxisId="t" orientation="left"
              tick={{ fontSize: 10, fill: "#94A3B8", fontFamily: "JetBrains Mono" }}
              label={{ value: "Mt", fill: "#00E5FF", fontSize: 10, angle: -90, dx: -6 }} />
            <YAxis yAxisId="g" orientation="right"
              tick={{ fontSize: 10, fill: "#94A3B8", fontFamily: "JetBrains Mono" }}
              label={{ value: "g", fill: "#FF5722", fontSize: 10, angle: 90, dx: 12 }} />
            <Tooltip
              contentStyle={{ background: "#0B0D12", border: "1px solid #2A2F3A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
              labelStyle={{ color: "#F1F5F9" }}
              formatter={(v, n) => (typeof v === "number" && /Mt$|Ore \(|Waste \(/i.test(n)
                ? [`${v.toFixed(2)} Mt`, n]
                : [v, n])}
            />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
            {snapPoint && (
              <ReferenceLine yAxisId="t" x={snapPoint.cutoff} stroke="#FF5722" strokeWidth={1.5} strokeDasharray="4 3">
                <RLabel value={`Cutoff ${cutoff.toFixed(2)}`} position="top"
                  fill="#FF5722" fontSize={10} fontFamily="JetBrains Mono" />
              </ReferenceLine>
            )}
            {snapPoint && (
              <ReferenceDot yAxisId="t" x={snapPoint.cutoff} y={(snapPoint.tonnage_kt || 0) / 1_000}
                r={5} fill="#FF5722" stroke="#0B0D12" strokeWidth={2} isFront />
            )}
            <Line yAxisId="t" type="monotone" dataKey="tonnage_mt" stroke="#00E5FF" strokeWidth={2} dot={false}
              name="Ore (Mt)" isAnimationActive={false} />
            <Line yAxisId="t" type="monotone" dataKey="waste_mt" stroke="#64748B" strokeWidth={2} dot={false} strokeDasharray="4 3"
              name="Waste (Mt)" isAnimationActive={false} />
            <Line yAxisId="g" type="monotone" dataKey="avg_grade" stroke="#FF5722" strokeWidth={2} dot={false}
              name="Avg grade" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* Legend + engineering description — no chart without a writeup */}
      <div className="mt-3 grid gap-2 border-t border-border pt-3 text-xs">
        <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-widest">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 bg-[#00E5FF]" /> Ore tonnage (Mt)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 border-t-2 border-dashed border-[#64748B]" /> Waste tonnage (Mt)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 bg-[#FF5722]" /> Average grade of ore ({gradeUnit})
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF5722]" /> Current cutoff snapshot
          </span>
        </div>
        <p className="text-muted-foreground leading-relaxed">
          The curve summarises every block in the model above the selected
          cutoff grade. As the cutoff rises, ore tonnage drops while the
          average grade of remaining ore climbs — the classic grade-tonnage
          trade-off used for cutoff optimisation. Numbers come from the
          server-side compute against the latest block model (no downsampling).
          The <span className="text-foreground">Above cutoff</span> figure is
          a raw grade filter; the <span className="text-foreground">Ore (SSoT)</span> figure
          is the material-class rollup that Reports / Portfolio / Dashboard
          all consume — they will differ whenever depth-overburden
          reassignment (or lithology rules) move some above-cutoff blocks out
          of the ore kind.
        </p>
      </div>
    </div>
    </FullscreenPanel>
  );
}
